import React from 'react';

import { Flex, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Namespace, TFunction } from 'react-i18next';
import { Link } from 'react-router-dom';

import { EpdDocumentKind, EpdRegistryRowSchema } from '@app/models/orders';
import { formatDate, formatDateAndTime, fromUtc } from '@app/services/date';
import { EpdStatusTag } from '@components/EpdStatusTag';

export const getColumns = (
  t: TFunction<Namespace<'epd'>>,
  kind: EpdDocumentKind,
  isCounterparty: boolean
): ColumnsType<EpdRegistryRowSchema> => [
  { key: 'number', dataIndex: 'number', title: t('number') },
  { key: 'doc_date', dataIndex: 'doc_date', title: t('documentDate'), render: (value) => formatDate(value) },
  { key: 'state', dataIndex: 'state', title: t('state'), render: (state) => <EpdStatusTag state={state} /> },
  {
    key: 'parties',
    title: t('documentParties'),
    render: (_, row) => (
      <Flex vertical>
        {row.parties.map((party) => (
          <Typography.Text key={party.role}>
            {t(`parties.${party.role}` as const)}: {party.name || '—'}
          </Typography.Text>
        ))}
      </Flex>
    ),
  },
  {
    key: 'discrepancy',
    dataIndex: 'discrepancy_at',
    title: t('discrepancy'),
    render: (value) => (value ? formatDateAndTime(fromUtc(value)) : '—'),
  },
  ...(kind === EpdDocumentKind.ForwardingReceipt
    ? [
        {
          key: 'parent_number',
          dataIndex: 'parent_number',
          title: t('parentNumber'),
          render: (value: string | null) => value || '—',
        },
      ]
    : []),
  {
    key: 'order',
    title: t('orderNumber'),
    render: (_, row) => {
      const order = row.order;
      const path = isCounterparty
        ? `/dashboard/counterparty-orders/${order.uid}`
        : order.process_uid
        ? `/dashboard/processes/${order.process_uid}/orders/${order.uid}`
        : order.project_uid
        ? `/dashboard/projects/${order.project_uid}/${order.uid}`
        : undefined;
      return order.is_visible && path ? <Link to={`${path}?tab=epd`}>{order.num}</Link> : order.num;
    },
  },
];
